/**
 * El cliente HTTP contra la API.
 *
 * ## Qué cambió al salir de Next, y qué no
 *
 * Antes quien hablaba con la API era el servidor de Next, que reenviaba a mano
 * las cookies del navegador con `cookies()` de `next/headers`. Ahora llama el
 * navegador, y las manda solo: `credentials: "include"`.
 *
 * Lo que **no** cambió es el modelo de seguridad, y es a propósito. Las rutas
 * son relativas —`/api/v1/...`, sin origen— porque el CDN sirve el shell y
 * reenvía `/api/*` al backend. Para el navegador nunca hay una petición entre
 * sitios, así que las cookies `httpOnly` con `SameSite=Lax` siguen viajando sin
 * abrir CORS ni relajar nada.
 *
 * Consecuencia buena: **no hay ninguna URL de backend en el bundle**, ni
 * variable de entorno que configurarla. Lo que no existe no se puede filtrar.
 */

export class UnauthenticatedError extends Error {}

/**
 * Renueva la sesión y reintenta, una sola vez.
 *
 * El token de acceso dura 15 minutos y el de refresco 30 días, pero **nadie
 * llamaba a renovar**: a los quince minutos de trabajar, todo empezaba a dar
 * 401 y la aplicación parecía caída. Lo peor es que no se veía como una sesión
 * vencida sino como errores sueltos en cada pantalla, así que la conclusión
 * natural era que se había roto algo.
 *
 * Una vez y no en bucle: si la renovación tampoco anda, la sesión se terminó
 * de verdad y hay que volver a entrar.
 *
 * Se comparte una sola promesa entre todas las llamadas que fallan a la vez.
 * Una pantalla dispara cinco consultas juntas; sin esto, las cinco pedirían
 * tokens nuevos y las últimas cuatro llegarían con el token que la primera
 * acaba de reemplazar.
 */
let renovando: Promise<boolean> | null = null;

async function renovarSesion(): Promise<boolean> {
  renovando ??= fetch("/api/v1/auth/refresh", {
    method: "POST",
    credentials: "include",
    cache: "no-store",
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      renovando = null;
    });
  return renovando;
}

/** Renueva la sesión sin reintentar nada: la usa la bandeja en vivo antes de reconectarse. */
export function refreshSession(): Promise<boolean> {
  return renovarSesion();
}

/** Error de la API con su mensaje ya legible, para mostrarlo en la UI. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
  detail?: unknown;
}

export async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {},
  { reintentar = true }: { reintentar?: boolean } = {},
): Promise<T> {
  const response = await fetch(path, {
    method: init.method ?? "GET",
    headers: init.body === undefined ? {} : { "content-type": "application/json" },
    // Mismo origen, pero explícito: sin esto el navegador no manda las cookies
    // en una petición hecha desde JavaScript.
    credentials: "include",
    // Datos de sesión: nunca se cachean en el nivel de HTTP. Quien cachea es
    // TanStack Query, que sabe cuándo invalidar.
    cache: "no-store",
    // Se agregan solo si existen: con `exactOptionalPropertyTypes`, pasar
    // `body: undefined` no es lo mismo que no pasar `body`.
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    ...(init.signal ? { signal: init.signal } : {}),
  });

  if (response.status === 401) {
    // La renovación no se intenta sobre el propio circuito de sesión: si
    // `/auth/refresh` da 401, reintentarlo es preguntar dos veces lo mismo.
    if (reintentar && !path.startsWith("/api/v1/auth/") && (await renovarSesion())) {
      return apiFetch<T>(path, init, { reintentar: false });
    }
    throw new UnauthenticatedError();
  }
  if (response.status === 204) {
    return undefined as T;
  }
  if (!response.ok) {
    throw new ApiError(await readErrorMessage(response), response.status);
  }
  return (await response.json()) as T;
}

/**
 * Sube un archivo. Va aparte de `apiFetch` porque con `FormData` el navegador
 * tiene que poner el `content-type` él —incluye el `boundary`—, y ponerlo a
 * mano rompe la carga.
 */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  // El método es del endpoint, no de la función. Estaba fijo en `POST` y la
  // imagen del agente se declara `PUT` —reemplaza un recurso que ya existe—,
  // así que cambiarla devolvía 405 sin que nada dijera por qué.
  { method = "POST", reintentar = true }: { method?: string; reintentar?: boolean } = {},
): Promise<T> {
  const response = await fetch(path, {
    method,
    body: form,
    credentials: "include",
    cache: "no-store",
  });

  if (response.status === 401) {
    if (reintentar && (await renovarSesion())) {
      return apiUpload<T>(path, form, { method, reintentar: false });
    }
    throw new UnauthenticatedError();
  }
  if (response.status === 204) return undefined as T;
  if (!response.ok) throw new ApiError(await readErrorMessage(response), response.status);
  return (await response.json()) as T;
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body.error?.message) return body.error.message;
    if (Array.isArray(body.detail) && body.detail.length > 0) {
      // Errores de validación de FastAPI: se muestra el primero, que suele
      // alcanzar para entender qué campo está mal.
      const first = body.detail[0] as { loc?: unknown[]; msg?: string };
      const field = Array.isArray(first.loc) ? first.loc.slice(1).join(".") : "";
      return field ? `${field}: ${first.msg}` : (first.msg ?? "Datos inválidos");
    }
  } catch {
    // Cuerpo no JSON: se cae al mensaje genérico.
  }
  return `La API respondió ${response.status}`;
}

/** Arma el sufijo `?a=1&b=2`, omitiendo lo vacío. Las listas repiten la clave. */
export function searchSuffix(
  // Un objeto de consulta cualquiera, no un `Record` con índice: los tipos de
  // filtro son interfaces cerradas y exigir el índice obligaría a enumerar los
  // campos en cada llamador — que es exactamente cómo se perdió `lifecycle`.
  entries: object,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(entries)) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.set(key, String(value));
    }
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}
